import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AddExistingProgramsParams } from "../../../../../../../../api/services/program/models";
import { useEligibleSearchProgramMutation } from "../../../../../../../../api/services/program/programApis";
import { useCreateUpdateChildProgramsMutation } from "../../../../../../../../api/services/project/projectApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  MuiModal,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import { ROLE_IDS } from "../../../../../../../../helpers/constants";
import {
  formatApiDataToDropDownOption,
  getFieldUndefinedForPayloadParams,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  AddExistingProgramsResultRow,
  AddExistingProgramsSearchFormKey,
  ChildProgramsListRowValue,
} from "../../../../../../modelsAndSchemas/Models";
import { AddExistingProgramsSearchSchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { addExistingProgramsSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { AddExistingProgramsWrapper } from "./AddExistingPrograms.style";
import { AddExistingProgramsSearchForm } from "./components/addExistingProgramsSearchForm/AddExistingProgramsSearchForm";
import { AddExistingProgramSearchResult } from "./components/addExistingProgramsSearchResult/AddExistingProgramsSearchResult";

export interface AddExistingProgramsProps {
  open: boolean;
  onClose: () => void;
  clientName: string;
  clientId: string;
  projectId?: string;
  preSelectedChildPrograms?: ChildProgramsListRowValue[];
}

export const AddExistingPrograms: React.FC<AddExistingProgramsProps> = ({
  onClose,
  open,
  clientName,
  projectId,
  preSelectedChildPrograms,
  clientId,
}) => {
  const {
    fetchDropdownValues,
    commonLabels,
    fetchComponentTitle,
    commonValidationMessages,
  } = useSiteCore();
  const { addExistingProgramsTitle, programStatusList: programStatusListKey } =
    addExistingProgramsSiteCoreKeys;
    
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [selectedPrograms, setSelectedPrograms] = useState<
    AddExistingProgramsResultRow[]
  >([]);
  const [mappedPreSelectedPrograms, setMappedPreSelectedPrograms] = useState<
    AddExistingProgramsResultRow[]
  >([]);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);

  useEffect(() => {
    if (isNotEmpty(preSelectedChildPrograms)) {
      const mappedResults = preSelectedChildPrograms.map((item) => {
        return {
          ...item,
          isChecked: true,
        };
      });
      setMappedPreSelectedPrograms(mappedResults);
    }
  }, [preSelectedChildPrograms]);

  const addExistingProgramsSearchDefaultValues = {
    [AddExistingProgramsSearchFormKey.client]: clientName,
    [AddExistingProgramsSearchFormKey.projectManagerId]: "",
    [AddExistingProgramsSearchFormKey.programParticipantName]: "",
    [AddExistingProgramsSearchFormKey.programParticipantNameFilter]:
      addExistingProgramsSiteCoreKeys.contains,
    [AddExistingProgramsSearchFormKey.keyNumber]: "",
    [AddExistingProgramsSearchFormKey.keyNumberFilter]:
      addExistingProgramsSiteCoreKeys.contains,
    [AddExistingProgramsSearchFormKey.clientManagedStatus]: "",
    [AddExistingProgramsSearchFormKey.programStatus]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: addExistingProgramsSearchDefaultValues,
    resolver: yupResolver(
      AddExistingProgramsSearchSchema(commonValidationMessages),
    ),
  });

  const {
    getValues,
    reset,
    formState: { isDirty },
  } = methods;

  const {
    mutateAsync: getEligibleProgramsList,
    isPending: isProgramsListPending,
    data: programsListData,
    isError: isProgramsListError,
  } = useEligibleSearchProgramMutation();

  const {
    mutateAsync: createUpdateSelectedChildPrograms,
    isPending: isCreateUpdateSelectedChildProgramsPending,
    isError: isCreateUpdateSelectedChildProgramsError,
    isSuccess: isCreateUpdateSelectedChildProgramsSuccess,
  } = useCreateUpdateChildProgramsMutation();

  const {
    mutateAsync: getProjectManagers,
    data: projectManagerData,
    isPending: isProjectManagerApiLoading,
    isError: isProjectManagerApiError,
  } = useGetUsersDetailByRoleMutation();

  const callGetExistingProgramsListApi = () => {
    const {
      projectManagerId,
      programParticipantName,
      programParticipantNameFilter,
      keyNumber,
      keyNumberFilter,
      clientManagedStatus,
      programStatus,
    } = getValues();

    let programStatusList: string[] = [programStatus];
    if (isEmpty(programStatus)) {
      programStatusList = fetchDropdownValues(
        addExistingProgramsTitle,
        programStatusListKey,
      )
        .filter((item) => item.value)
        .map((item) => item.value as string);
    }

    const params: AddExistingProgramsParams = {
      clientId: getValueOrBlank(clientId),
      programStatuses: programStatusList,
      getUntaggedPrograms: true,
      projectId: getValueOrBlank(projectId),
      ...getFieldUndefinedForPayloadParams({
        projectManagerId,
        keyNumber,
        keyNumberFilter,
        clientManagedStatus,
        programName: programParticipantName,
        programNameFilter: programParticipantNameFilter,
      }),
    };
    getEligibleProgramsList(params);
  };

  useEffect(() => {
    callGetExistingProgramsListApi();
    getProjectManagers({ roleIds: [ROLE_IDS.projectManager] });
  }, []);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isProjectManagerApiLoading ||
          isProgramsListPending ||
          isCreateUpdateSelectedChildProgramsPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [
    isProjectManagerApiLoading,
    isProgramsListPending,
    isCreateUpdateSelectedChildProgramsPending,
  ]);

  useEffect(() => {
    if (
      isProjectManagerApiError ||
      isProgramsListError ||
      isCreateUpdateSelectedChildProgramsError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isProjectManagerApiError,
    isProgramsListError,
    isCreateUpdateSelectedChildProgramsError,
  ]);

  useEffect(() => {
    if (isCreateUpdateSelectedChildProgramsSuccess) {
      // Invalidate query to trigger automatic refetch
      queryClient.invalidateQueries({
        queryKey: ["useGetSearchProgramsForProjectMutation", projectId],
      });
      onClose();
    }
  }, [isCreateUpdateSelectedChildProgramsSuccess]);

  const handleAddButtonClick = () => {
    if (isNotEmpty(projectId) && isNotEmpty(selectedPrograms)) {
      const selectedProgramIds = selectedPrograms
        .filter((row: AddExistingProgramsResultRow) => row.isChecked)
        .map((row: AddExistingProgramsResultRow) => row.id);
      createUpdateSelectedChildPrograms({
        projectId: projectId,
        programId: selectedProgramIds,
      });
    }
  };

  const handleCancelButtonClick = () => {
    if (isDirty) {
      setShowCancelConfirmationModal(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmationModalPrimaryClick = () => {
    onClose?.();
  };

  return (
    <MuiModal
      hasLargeContent
      open={open}
      onClose={handleCancelButtonClick}
      title={fetchComponentTitle(addExistingProgramsTitle)}
      primaryButtonTitle={commonLabels.add}
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={handleAddButtonClick}
      onSecondaryButtonClick={handleCancelButtonClick}
    >
      <FormProvider {...methods}>
        <AddExistingProgramsWrapper>
          <AddExistingProgramsSearchForm
            projectManagerData={formatApiDataToDropDownOption(
              projectManagerData,
            )}
            handleClearAllSearch={() => reset()}
            handleSearchClick={callGetExistingProgramsListApi}
          />
          <AddExistingProgramSearchResult
            rows={
              (programsListData?.data as AddExistingProgramsResultRow[]) ?? []
            }
            setSelectedPrograms={setSelectedPrograms}
            preSelectedChildPrograms={mappedPreSelectedPrograms}
          />

          {showCancelConfirmationModal && (
            <WarningConfirmationModal
              show={showCancelConfirmationModal}
              modalTitle={commonLabels.confirmation}
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
              onClose={() => setShowCancelConfirmationModal(false)}
              onPrimaryButtonClick={handleConfirmationModalPrimaryClick}
              labelContent={commonLabels.discardChangesText}
            />
          )}
        </AddExistingProgramsWrapper>
      </FormProvider>
    </MuiModal>
  );
};
